import { trackEvent } from "../../backend/utils/helper-methods";
import type { StudyMaterial } from "./materials";

/**
 * What a learner does after the starting page ("what would you like to study?"), for conversion analysis:
 *
 *   Study Start Option Chosen   the choice (materials.ts)
 *   Study Start First Action    the first real study action afterwards: a question answered or a study-guide
 *                               section viewed, whether it matches the choice, and how long it took
 *   Study Start Crossed Over    the first use of the OTHER material within 7 days of the choice
 *
 * One journey per browser (the latest start-page choice), kept in localStorage. Question answers are not tied
 * to a deck at the point we observe them, so any answer after a choice counts as that journey's question use;
 * study-guide views must match the journey's deck. Content-free: ids are never sent.
 */
type Journey = { deckId: string; option: StudyMaterial; chosenAt: number; firstAction?: StudyMaterial; firstActionAt?: number; crossedOver?: boolean };

const STORAGE_KEY = "jungle.study_start.journey";
const JOURNEY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

function read(): Journey | null {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null");
    if (!value || typeof value.deckId !== "string" || (value.option !== "questions" && value.option !== "study_guide")) return null;
    return Date.now() - value.chosenAt > JOURNEY_WINDOW_MS ? null : value;
  } catch { return null; }
}

function write(journey: Journey) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(journey)); } catch { /* private mode: no journey */ }
}

export function elapsedBucket(milliseconds: number): string {
  const seconds = milliseconds / 1000;
  if (seconds < 30) return "0_29s";
  if (seconds < 120) return "30s_2m";
  if (seconds < 600) return "2_10m";
  if (seconds < 3600) return "10_60m";
  if (seconds < 86400) return "1_24h";
  return "1_7d";
}

/** Called when the learner picks an option on the starting page. */
export function startStudyJourney(deckId: string, option: StudyMaterial) {
  write({ deckId, option, chosenAt: Date.now() });
}

/** Called on each study action; fires the first-action and cross-over events at most once each. */
export function recordStudyAction(material: StudyMaterial, deckId?: string, user?: unknown) {
  const journey = read();
  if (!journey) return;
  // A study-guide view must be for the journey's own deck; question answers carry no deck here.
  if (material === "study_guide" && deckId !== journey.deckId) return;
  const now = Date.now();
  if (!journey.firstAction) {
    journey.firstAction = material; journey.firstActionAt = now; write(journey);
    void trackEvent("Study Start First Action", {
      source_option: journey.option, action: material, matches_choice: material === journey.option,
      time_to_first_action_bucket: elapsedBucket(now - journey.chosenAt),
    }, user as any);
    return;
  }
  if (material !== journey.firstAction && !journey.crossedOver) {
    journey.crossedOver = true; write(journey);
    void trackEvent("Study Start Crossed Over", {
      source_option: journey.option, first_action: journey.firstAction, to: material,
      time_since_choice_bucket: elapsedBucket(now - journey.chosenAt),
    }, user as any);
  }
}
